/**
 * Warehouse identity & key derivation for the account model.
 *
 * account_key    = `${type}|${norm(hostDim)}|${norm(dbDim)}`   — the bucket
 * connection_key = `${account_key}|`                           — one connection per bucket (no schema)
 *
 * Per-warehouse behaviour lives in a small **strategy** object (one per warehouse
 * type), registered in `STRATEGIES`. Each strategy owns:
 *   - the backend `type` strings it handles (incl. aliases like REDSHIFT→RS),
 *   - identifier case sensitivity (BigQuery project / Databricks identifiers preserve
 *     case; Snowflake / Redshift / Postgres are case-insensitive → lowercased),
 *   - the non-credential `safeConfigKeys` persisted from the synced destination config,
 *   - `extractDims(config)` → the bucket `(host, db)` identity, handling per-type
 *     *flavours* internally (e.g. Redshift provisioned-by-host vs provisioned-by-cluster-id
 *     vs serverless-by-workgroup).
 *
 * Adding a new warehouse type = add one strategy object and register it. Nothing else
 * in the derivation path is type-aware.
 */

/**
 * Every driver-registry key. Closed so that a strategy cannot name a driver that does not exist, and
 * the registry in `query.ts` (`Record<WarehouseDriverType, WarehouseDriver>`) cannot miss one: adding
 * a warehouse type means adding it here, and then the compiler asks for its driver.
 */
export const WAREHOUSE_DRIVER_TYPES = ['POSTGRES', 'RS', 'SNOWFLAKE', 'BIGQUERY', 'DATABRICKS', 'CLICKHOUSE'] as const;

export type WarehouseDriverType = (typeof WAREHOUSE_DRIVER_TYPES)[number];

export function isWarehouseDriverType(type: string): type is WarehouseDriverType {
  return (WAREHOUSE_DRIVER_TYPES as readonly string[]).includes(type);
}

export interface WarehouseStrategy {
  /**
   * Canonical type used as the `account_key` prefix, so aliases collapse to one bucket
   * (REDSHIFT and RS, DELTALAKE and DATABRICKS, BIGQUERY and BQ → the same account).
   */
  readonly canonicalType: string;
  /**
   * Driver-registry key / persisted-type string (the "BIGQUERY"-style vocabulary), as distinct
   * from `canonicalType` (the "BQ"-style account-key prefix). Single source of truth for driver
   * dispatch ({@link driverTypeForWarehouse}) so that alias table isn't hand-maintained separately.
   */
  readonly driverType: WarehouseDriverType;
  /** Backend `type` strings (any case) this strategy handles, including aliases. */
  readonly aliases: readonly string[];
  /**
   * False → identifiers are case-insensitive and lowercased before keying
   * (Snowflake, Redshift, Postgres). True → preserve case (BigQuery project,
   * Databricks host/catalog).
   */
  readonly caseSensitive: boolean;
  /** Case policy for the host dim only. Defaults to `caseSensitive`. */
  readonly hostCaseSensitive?: boolean;
  /** Non-credential config fields to persist at sync (the dims + display fields). */
  readonly safeConfigKeys: readonly string[];
  /**
   * When true, an empty db dim is valid (e.g. Databricks without Unity Catalog).
   * Default false — host and db are both required for key derivation.
   */
  readonly dbDimOptional?: boolean;
  /**
   * Derive the bucket dims `(host, db)` from a sanitized config, or null when the
   * config lacks a usable identity. Handles per-flavour variants internally.
   */
  extractDims: (config: Record<string, string>) => { host: string; db: string } | null;
}

const field = (config: Record<string, string>, key: string): string => config[key]?.trim() ?? '';

const SNOWFLAKE: WarehouseStrategy = {
  canonicalType: 'SNOWFLAKE',
  driverType: 'SNOWFLAKE',
  aliases: ['SNOWFLAKE'],
  caseSensitive: false,
  safeConfigKeys: ['account', 'database', 'warehouse', 'namespace', 'role', 'user'],
  extractDims: (c) => {
    const host = field(c, 'account');
    const db = field(c, 'database');
    return host && db ? { host, db } : null;
  },
};

const REDSHIFT: WarehouseStrategy = {
  canonicalType: 'RS',
  driverType: 'RS',
  aliases: ['RS', 'REDSHIFT'],
  caseSensitive: false,
  safeConfigKeys: [
    'host',
    'port',
    'database',
    'user',
    'namespace',
    'clusterId',
    'clusterRegion',
    'workgroupName',
    'iamRoleARNForAuth',
    'useIAMForAuth',
  ],
  // Flavours: provisioned-by-host (host endpoint present), provisioned-by-cluster-id
  // (host blank, clusterId set), and serverless (host blank, workgroupName set). The
  // first available identifier becomes the host-dim so every flavour forms a bucket;
  // the real endpoint is entered in the connection builder when sync didn't provide one.
  extractDims: (c) => {
    const host = field(c, 'host') || field(c, 'clusterId') || field(c, 'workgroupName');
    const db = field(c, 'database');
    return host && db ? { host, db } : null;
  },
};

const DATABRICKS: WarehouseStrategy = {
  canonicalType: 'DATABRICKS',
  driverType: 'DATABRICKS',
  aliases: ['DATABRICKS', 'DELTALAKE'],
  caseSensitive: true,
  dbDimOptional: true,
  safeConfigKeys: ['host', 'path', 'httpPath', 'catalog', 'namespace'],
  // Catalog is optional in RudderStack (workspace default applies at query time). When absent,
  // the db dim is empty → account_key = `DATABRICKS|host|`.
  extractDims: (c) => {
    const host = field(c, 'host');
    if (!host) return null;
    return { host, db: field(c, 'catalog') };
  },
};

const BIGQUERY: WarehouseStrategy = {
  canonicalType: 'BQ',
  driverType: 'BIGQUERY',
  aliases: ['BQ', 'BIGQUERY'],
  caseSensitive: true,
  safeConfigKeys: ['project', 'location', 'namespace'],
  // BigQuery has no separate database — the project is both host- and db-dim.
  extractDims: (c) => {
    const project = field(c, 'project');
    return project ? { host: project, db: project } : null;
  },
};

const POSTGRES: WarehouseStrategy = {
  canonicalType: 'POSTGRES',
  driverType: 'POSTGRES',
  aliases: ['POSTGRES'],
  caseSensitive: false,
  safeConfigKeys: ['host', 'port', 'database', 'user', 'namespace', 'sslMode'],
  extractDims: (c) => {
    const host = field(c, 'host');
    const db = field(c, 'database');
    return host && db ? { host, db } : null;
  },
};

const CLICKHOUSE: WarehouseStrategy = {
  canonicalType: 'CLICKHOUSE',
  driverType: 'CLICKHOUSE',
  aliases: ['CLICKHOUSE'],
  // Hostnames fold case; ClickHouse database names do not.
  caseSensitive: true,
  hostCaseSensitive: false,
  safeConfigKeys: ['host', 'port', 'database', 'user', 'namespace', 'secure', 'skipVerify', 'rudderSchema'],
  extractDims: (c) => {
    const host = field(c, 'host');
    const db = field(c, 'database') || field(c, 'namespace');
    return host && db ? { host, db } : null;
  },
};

const STRATEGIES: readonly WarehouseStrategy[] = [SNOWFLAKE, REDSHIFT, DATABRICKS, BIGQUERY, POSTGRES, CLICKHOUSE];

/** Every registered strategy, for contract tests that must cover each warehouse type. */
export function allWarehouseStrategies(): readonly WarehouseStrategy[] {
  return STRATEGIES;
}

const BY_TYPE = new Map<string, WarehouseStrategy>();
for (const strategy of STRATEGIES) {
  for (const alias of strategy.aliases) BY_TYPE.set(alias.toUpperCase(), strategy);
}

/** Resolve the strategy for a backend warehouse type (alias-aware); null if unsupported. */
export function warehouseStrategy(type: string): WarehouseStrategy | null {
  return BY_TYPE.get(type.toUpperCase()) ?? null;
}

/**
 * Map any backend warehouse type (or alias) to its driver-registry key — the single source for
 * driver dispatch. Unknown types pass through up-cased so the driver lookup can raise its own
 * "Unsupported warehouse type" error. Defaults a missing type to POSTGRES (legacy behaviour).
 */
export function driverTypeForWarehouse(type: string | undefined): string {
  const upper = (type ?? 'POSTGRES').toUpperCase();
  return warehouseStrategy(upper)?.driverType ?? upper;
}

function normDim(value: string, strategy: WarehouseStrategy, dim: 'host' | 'db'): string {
  const v = value.trim();
  const sensitive = dim === 'host' ? (strategy.hostCaseSensitive ?? strategy.caseSensitive) : strategy.caseSensitive;
  return sensitive ? v : v.toLowerCase();
}

/**
 * Assemble `${canonicalType}|${host}|${db}` from already-normalized dims, rejecting any dim
 * that contains the `|` delimiter. Conformant warehouse identifiers never contain `|`, so this
 * is fail-closed insurance: a stray `|` would desync the positional `split('|')` parsing that
 * five call sites use to recover host/db from a key. A rejected dim → null (unmappable), never a
 * silently corrupt key.
 */
function assembleAccountKey(canonicalType: string, host: string, db: string): string | null {
  if (host.includes('|') || db.includes('|')) return null;
  return `${canonicalType}|${host}|${db}`;
}

/** Derive the bucket identity key from a warehouse type and its sanitized config. */
export function deriveAccountKey(type: string, config: Record<string, string> | null): string | null {
  if (!config) return null;
  const strategy = warehouseStrategy(type);
  if (!strategy) return null;
  const dims = strategy.extractDims(config);
  if (!dims) return null;
  return assembleAccountKey(
    strategy.canonicalType,
    normDim(dims.host, strategy, 'host'),
    normDim(dims.db, strategy, 'db'),
  );
}

/**
 * Derive the bucket identity key from explicit, client-supplied host/db dims.
 *
 * Used by the save path (`updateConnection`), where the client sends the raw dims
 * (from `listBuckets`) and the server — never trusting a client-sent `account_key` —
 * constructs the key itself with the same type-aware normalization as
 * {@link deriveAccountKey}. Returns null for an unsupported type, blank hostDim, or
 * blank dbDim when the strategy does not set {@link WarehouseStrategy.dbDimOptional}.
 */
export function deriveAccountKeyFromDims(type: string, hostDim: string, dbDim: string): string | null {
  const strategy = warehouseStrategy(type);
  if (!strategy) return null;
  if (!hostDim.trim()) return null;
  if (!dbDim.trim() && !strategy.dbDimOptional) return null;
  return assembleAccountKey(strategy.canonicalType, normDim(hostDim, strategy, 'host'), normDim(dbDim, strategy, 'db'));
}

/** The connection key: one per account bucket. The trailing `|` keeps legacy keys and rETL externalIds stable. */
export function deriveConnectionKey(accountKey: string): string {
  return `${accountKey}|`;
}

/**
 * Return the safe database/catalog dimension from an account key (`TYPE|host|db`). Account keys are
 * assembled only after rejecting the delimiter in every dimension, so exactly three parts is both a
 * format check and a guarantee that the returned value is not credential material. Databricks may
 * intentionally have an empty catalog dimension.
 */
export function catalogNameFromAccountKey(accountKey: string | null | undefined): string | null {
  if (!accountKey) return null;
  const parts = accountKey.split('|');
  if (parts.length !== 3) return null;
  const catalog = parts[2]?.trim();
  return catalog || null;
}

/**
 * The BigQuery PROJECT from an account key (`BQ|project|project`) — and `null` for every other
 * family, by construction rather than by convention.
 *
 * This is the only reader that puts a key's index 1 in front of the MODEL or the public MCP, and the
 * prefix check is what keeps it that way. Index 1 is read elsewhere — `getWarehouseBuckets`
 * (`db/warehouses.ts`), `clearIamCacheForAccountKey` (`services/warehouse-connection.ts`) and three UI
 * call sites (`WarehouseAccountCard`, `useWarehouseAccounts`, `addAccount/WarehouseStep`) all split a
 * key back into its dims — but each of those hands the value to the operator who typed it or back to
 * the server that normalized it, never to an agent and never into an audience's coordinates.
 *
 * The general rule this carves an exception out of is real and still binds the other four: a
 * key's host dim is a normalized BUCKET dim, not the endpoint an rETL account dials. A Redshift
 * cluster id or serverless workgroup stands in for a host; a Snowflake account identifier is not a
 * hostname; both are lower-cased on the way in (`caseSensitive: false`). Comparing any of those
 * against an account's real endpoint manufactures a mismatch out of a working setup, which is why
 * `resolveAudienceWarehouseCoordinates` leaves the host dim out of its fallbacks.
 *
 * BigQuery is exempt because all three premises fail for it, not because it is convenient:
 *
 * - {@link BIGQUERY}`.extractDims` returns the project as BOTH dims — "BigQuery has no separate
 *   database" — so index 1 IS the project, standing in for nothing.
 * - `caseSensitive: true`, so `normDim` does not fold it; the stored dim is the project verbatim.
 * - The save path pins the two together: `assertBigQueryConfigMatchesBucketDims` (rpc/procedures/
 *   warehouses.ts) rejects any BigQuery connection whose bucket dims and config name different
 *   projects, so a stored `BQ|…` key's index 1 is the project the connection actually queries.
 *
 * Nor is it credential-adjacent the way the refused dims are: a BigQuery project is the first
 * segment of every table path the audience SQL already spells out.
 *
 * If you are here to promote another family, none of the above transfers — check its `extractDims`,
 * its `caseSensitive`, and whether any save path pins its host dim to the config, before widening
 * this by a single prefix.
 */
export function bigQueryProjectFromAccountKey(accountKey: string | null | undefined): string | null {
  if (!accountKey) return null;
  const parts = accountKey.split('|');
  if (parts.length !== 3 || parts[0] !== BIGQUERY.canonicalType) return null;
  const project = parts[1]?.trim();
  return project || null;
}

/**
 * The ClickHouse HOST from an account key (`CLICKHOUSE|host|database`), and `null` for every other
 * family. Like {@link bigQueryProjectFromAccountKey}, it reads index 1 only where that dim is the real
 * endpoint: a ClickHouse key's host dim is the host the connection dials, folded to lower case
 * (`hostCaseSensitive: false`), and the rETL host comparison ignores case. The database dim keeps
 * its case (`caseSensitive: true`), so {@link catalogNameFromAccountKey} returns it verbatim.
 */
export function clickHouseHostFromAccountKey(accountKey: string | null | undefined): string | null {
  if (!accountKey) return null;
  const parts = accountKey.split('|');
  if (parts.length !== 3 || parts[0] !== CLICKHOUSE.canonicalType) return null;
  const host = parts[1]?.trim();
  return host || null;
}

/**
 * A Lookout-minted RudderStack account name: `lookout-<workspaceId>-<connectionKey>` (see
 * `lookoutAccountName` in `lib/audience/rs-account-mirror.ts`). The connection key embeds the host, so
 * such a name is never shown to a user or the agent. A connection key always contains `|`.
 */
export function isLookoutMintedAccountName(name: string | null | undefined): boolean {
  // Trimmed here too, so no caller can pass a padded minted name and have it read as a customer name.
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return trimmed.startsWith('lookout-') && trimmed.includes('|');
}

/** Inputs for {@link connectionDisplayLabel}. No schema: connections are schema-agnostic (decision L). */
export interface ConnectionDisplayLabelInput {
  /** `warehouse_connections.rs_account_name`. A label, never an identity, never a log field. */
  rsAccountName?: string | null;
  /** The resolved destination's own name, when one resolves. */
  destinationName?: string | null;
  accountKey: string | null | undefined;
  type: string;
  /**
   * Phase 3d: the bound account's stored database name, VERBATIM, when one was resolved and
   * may be presented as the account's own (`services/warehouse-database-label.ts` decides that — it is
   * `unverifiable` for Redshift and Databricks, whose option spellings are still guesses). Omitted or
   * `null` keeps today's behaviour exactly.
   *
   * It outranks the account key's db dim because that dim is a BUCKET key: `normDim` has case-folded
   * it for Snowflake, Redshift, Postgres and ClickHouse, so an operator who created `Analytics_PROD` is
   * shown `analytics_prod`. Right for a key, wrong for a name shown to a person.
   *
   * **Label only.** Nothing here reaches SQL — which spelling is GENERATED is still an open question.
   */
  accountDatabaseLabel?: string | null;
}

/**
 * What a user or the agent sees for a connection (decision F): the bound account's name unless it is
 * Lookout-minted, else the resolved destination's name, else the database, else the type. Never the
 * host. Not unique — run a LIST through {@link disambiguateConnectionNames}. Never put it in a log line.
 */
export function connectionDisplayLabel(input: ConnectionDisplayLabelInput): string {
  const account = input.rsAccountName?.trim();
  if (account && !isLookoutMintedAccountName(account)) return account;
  const destination = input.destinationName?.trim();
  if (destination) return destination;
  const accountDatabase = input.accountDatabaseLabel?.trim();
  if (accountDatabase) return accountDatabase;
  return catalogNameFromAccountKey(input.accountKey) ?? input.type;
}

/** One rendered connection label plus the connection's surrogate uuid, for {@link disambiguateConnectionNames}. */
export interface ConnectionNameEntry {
  /** The label as already rendered — a destination's own name, or {@link connectionDisplayLabel}. */
  name: string;
  /** The connection's surrogate uuid. Absent/blank means no suffix can be minted for this entry. */
  id: string | null | undefined;
}

/** How many characters of the uuid the suffix starts at, per the reviewer's `analytics (a3f1)`. */
const SUFFIX_START_LENGTH = 4;

/**
 * Disambiguate a LIST of connection labels, suffixing only the ones that would otherwise be identical.
 *
 * {@link connectionDisplayLabel} is per-connection and cannot see a collision, and the collision is real:
 * connections are keyed by account bucket only, so there is one per bucket. Every collision is therefore
 * ACROSS buckets (or between account/destination names that merely match), where the sole differing dim
 * is the host, and R1 (design §6.4) forbids printing it. `POSTGRES|prod-a|analytics`
 * and `POSTGRES|prod-b|analytics` both render `analytics`, and the model is handed two identical handles.
 *
 * The suffix does NOT say which connection is which — it only makes the two handles distinct, so a picker
 * (human or model) can name one of them unambiguously. Unique labels are left alone: `analytics` stays
 * `analytics`, and becomes `analytics (a3f1)` only when something else in the same list renders `analytics`.
 *
 * Collides on the FINAL label whatever its source, because a connection backed by a RudderStack
 * destination inherits the name the customer typed there and two destinations may well share one.
 *
 * Returns one label per input, in input order.
 */
export function disambiguateConnectionNames(entries: readonly ConnectionNameEntry[]): string[] {
  const byName = new Map<string, number[]>();
  entries.forEach((entry, index) => {
    const group = byName.get(entry.name);
    if (group) group.push(index);
    else byName.set(entry.name, [index]);
  });

  const labels = entries.map((entry) => entry.name);
  for (const indices of byName.values()) {
    if (indices.length < 2) continue;

    // Dashes carry no identity, so strip them: `a3f1c0de-…` suffixes as `a3f1`, not `a3f-`. An entry
    // with no uuid gets NO suffix — never `()` or `(undefined)` — and stays ambiguous, which is the
    // honest outcome when there is nothing stable to name it by.
    const compact = indices.map((i) => (entries[i]?.id ?? '').replace(/-/g, ''));
    const usable = compact.filter((c) => c.length > 0);

    // Lengthen until every suffix in the group is distinct rather than print the same one twice.
    const maxLength = Math.max(...usable.map((c) => c.length), SUFFIX_START_LENGTH);
    let length = SUFFIX_START_LENGTH;
    while (length < maxLength && new Set(usable.map((c) => c.slice(0, length))).size < new Set(usable).size) {
      length += 1;
    }

    const proposed = indices.map((entryIndex, groupIndex) => {
      const suffix = compact[groupIndex]?.slice(0, length);
      return suffix ? `${entries[entryIndex]?.name ?? ''} (${suffix})` : (entries[entryIndex]?.name ?? '');
    });

    // Suffix only when it actually separates. One connection the resolution query fanned out arrives
    // several times under ONE uuid, so every proposal in the group is the same string — noise that
    // tells the reader nothing. Leave those bare rather than decorate an ambiguity we cannot resolve.
    if (new Set(proposed).size < 2) continue;
    indices.forEach((entryIndex, groupIndex) => {
      labels[entryIndex] = proposed[groupIndex] ?? labels[entryIndex] ?? '';
    });
  }
  return labels;
}

/** Returns true if the string starts with '@', which is the reserved sentinel prefix. */
export function isReservedSentinelPrefix(s: string): boolean {
  return s.startsWith('@');
}
